import { useEditor } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';
import { TRANSITIONS, TRANSITION_LABEL } from '../../lib/transitions';

/** Format ms as s.mmm for the compact time fields. */
function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Transitions tab - add a visual transition anywhere on the timeline and tune
 * its start + duration. Placement/edits write the project's transitions list
 * (undoable, persisted, previewed live on the stage via TransitionOverlay).
 */
export function StudioTransitionsPanel() {
  const transitions = useEditor((s) => s.transitions);
  const durationMs = useEditor((s) => s.durationMs);
  const selectedId = useEditor((s) => s.selectedTransitionId);
  const addTransition = useEditor((s) => s.addTransition);
  const updateTransition = useEditor((s) => s.updateTransition);
  const removeTransition = useEditor((s) => s.removeTransition);
  const selectTransition = useEditor((s) => s.selectTransition);

  const dur = Math.max(durationMs, transitions.at(-1)?.startMs ?? 0, 1000);

  const add = (kind: (typeof TRANSITIONS)[number]['kind']) => {
    addTransition(kind, playbackClock.timeMs());
  };
  const pick = (id: string, startMs: number) => {
    selectTransition(id);
    playbackClock.seek(startMs);
  };

  return (
    <div className="slp-body shk">

      <span className="str-section">Add at playhead</span>
      <div className="str-gallery">
        {TRANSITIONS.map((t) => (
          <button key={t.kind} className="str-card" onClick={() => add(t.kind)} title={t.desc}>
            <span className={`str-thumb str-${t.kind}`} aria-hidden />
            <span className="str-card-name">{t.label}</span>
          </button>
        ))}
      </div>

      <span className="str-section">
        On timeline{transitions.length ? ` · ${transitions.length}` : ''}
      </span>
      {transitions.length === 0 ? (
        <div className="str-empty">No transitions yet. Add one above.</div>
      ) : (
        <div className="str-list">
          {transitions.map((tr) => {
            const on = tr.id === selectedId;
            return (
              <div key={tr.id} className={on ? 'str-item on' : 'str-item'}>
                <button className="str-item-head" onClick={() => pick(tr.id, tr.startMs)}>
                  <span className={`str-dot str-${tr.kind}`} />
                  <span className="str-item-name">{TRANSITION_LABEL[tr.kind]}</span>
                  <span className="str-item-time">
                    {secs(tr.startMs)} → {secs(tr.startMs + tr.durationMs)}
                  </span>
                </button>
                {on && (
                  <div className="str-edit">
                    <div className="shk-row">
                      <span>Start</span>
                      <div className="shk-size">
                        <input
                          type="range"
                          min={0}
                          max={dur}
                          step={10}
                          value={tr.startMs}
                          onChange={(e) => updateTransition(tr.id, { startMs: Number(e.target.value) })}
                        />
                        <span>{secs(tr.startMs)}</span>
                      </div>
                    </div>
                    <div className="shk-row">
                      <span>Duration</span>
                      <div className="shk-size">
                        <input
                          type="range"
                          min={100}
                          max={3000}
                          step={50}
                          value={tr.durationMs}
                          onChange={(e) => updateTransition(tr.id, { durationMs: Number(e.target.value) })}
                        />
                        <span>{secs(tr.durationMs)}</span>
                      </div>
                    </div>
                    <div className="str-edit-actions">
                      <select
                        className="ses-select"
                        value={tr.kind}
                        onChange={(e) => updateTransition(tr.id, { kind: e.target.value as typeof tr.kind })}
                      >
                        {TRANSITIONS.map((t) => (
                          <option key={t.kind} value={t.kind}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <button className="str-remove" onClick={() => removeTransition(tr.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
